import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';

import { Field, reduxForm, SubmissionError } from 'redux-form';

import TextField from 'components/fields/TextField';
import IconCheck from 'components/icons/IconCheck';
import IconCrossBold from 'components/icons/IconCrossBold';

import * as validate from 'utils/validate';
import passwordRules from 'config/passwordRules';

import pxToRem from 'styles/pxToRem';

import { changeUserPassword } from 'redux/modules/users';

const styles = theme => ({
  grid: {
    marginTop: 10,
    '&:first-child': {
      marginTop: 0,
    },
  },
  typographyText: {
    fontSize: pxToRem(13),
    margin: '0 auto 15px',
  },
  iconCrossBold: {
    width: 10,
    height: 10,
    color: theme.palette.validation[500],
    marginRight: 2,
  },
  iconCheck: {
    width: 12,
    height: 12,
    color: theme.palette.active[500],
    marginRight: 0,
  },
  listItem: {
    paddingTop: 4,
    paddingBottom: 4,
  },
  listItemTextInvalid: {
    color: theme.palette.validation[500],
  },
});

function onSubmit(values, dispatch) {
  return new Promise((resolve, reject) => {
    dispatch(changeUserPassword({
      values: {
        ...values,
      },
      resolve,
      reject,
    }));
  }).then(res => res,
    (err) => {
      throw new SubmissionError(err);
    });
}

function validateForm(values) {
  const errors = {};
  if (values.Password) {
    // eslint-disable-next-line no-underscore-dangle
    errors._error = [
      ...Boolean(validate.containsUppercaseAlphabetical(values.Password)) && [
        'containsUppercaseAlphabetical',
      ],
      ...Boolean(validate.containsLowercaseAlphabetical(values.Password)) && [
        'containsLowercaseAlphabetical',
      ],
      ...Boolean(validate.containsNumber(values.Password)) && [
        'containsNumber',
      ],
      ...Boolean(validate.containsSpecialChar(values.Password)) && [
        'containsSpecialChar',
      ],
      ...Boolean(validate.minLength8(values.Password)) && [
        'minLength8',
      ],
    ];

    // eslint-disable-next-line no-underscore-dangle
    if (errors._error.length === 0) {
      // eslint-disable-next-line no-underscore-dangle
      delete errors._error;
    }
  }
  if (values.Password && values['Repeat Password']) {
    if (values.Password !== values['Repeat Password']) {
      errors['Repeat Password'] = 'Passwords do not match';
    } else {
      errors['Repeat Password'] = validate.password(values['Repeat Password']);
    }
  } else if (values['Repeat Password']) {
    errors['Repeat Password'] = 'Please, fill Password field first';
  }
  return errors;
}

const PasswordChangeForm = ({
  handleSubmit,
  className,
  dirty,
  error,
  title,
  classes,
}) => (
  <form
    noValidate
    autoComplete="off"
    onSubmit={handleSubmit}
    className={className}
  >
    {(dirty || title) &&
      <Typography
        className={classes.typographyText}
        align="center"
      >
        {dirty ? 'Password must contain the following:' : title}
      </Typography>
    }
    {dirty &&
      <List disablePadding>
        {passwordRules.map(item => (
          <ListItem
            className={classes.listItem}
            key={item.text}
            disableGutters
            dense
          >
            <ListItemIcon>
              {error && error.indexOf(item.name) !== -1 ? (
                <IconCrossBold className={classes.iconCrossBold} />
              ) : (
                <IconCheck className={classes.iconCheck} />
              )}
            </ListItemIcon>
            <ListItemText
              classes={{
                primary: classNames({
                  [classes.listItemTextInvalid]: item.invalid,
                }),
              }}
              primary={item.text}
            />
          </ListItem>
        ))}
      </List>
    }
    <Grid
      className={classes.grid}
      container
      justify="center"
      spacing={24}
    >
      <Grid item xs={12}>
        <Field
          id="Password"
          name="Password"
          label="Password"
          component={TextField}
          validate={[
            validate.required,
            validate.noCyrillic,
            validate.password,
          ]}
          type="password"
          fullWidth
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      justify="center"
      spacing={24}
    >
      <Grid item xs={12}>
        <Field
          id="Repeat Password"
          name="Repeat Password"
          label="Repeat Password"
          component={TextField}
          validate={[
            validate.required,
            validate.noCyrillic,
          ]}
          type="password"
          fullWidth
        />
      </Grid>
    </Grid>
  </form>
);

PasswordChangeForm.propTypes = {
  error: PropTypes.arrayOf(PropTypes.string),
  handleSubmit: PropTypes.func.isRequired,
  className: PropTypes.string,
  title: PropTypes.string,
  dirty: PropTypes.bool.isRequired,
  classes: PropTypes.shape({
    grid: PropTypes.string.isRequired,
    typographyText: PropTypes.string.isRequired,
    iconCrossBold: PropTypes.string.isRequired,
    iconCheck: PropTypes.string.isRequired,
    listItem: PropTypes.string.isRequired,
    listItemTextInvalid: PropTypes.string.isRequired,
  }).isRequired,
};

PasswordChangeForm.defaultProps = {
  className: '',
  error: null,
  title: null,
};

export default reduxForm({
  onSubmit,
  validate: validateForm,
})(withStyles(styles)(PasswordChangeForm));



// WEBPACK FOOTER //
// ./src/components/account/PasswordChangeDialog/PasswordChangeForm.js